import { Field } from 'formik';

import { PreferredUnitSystem } from '@frontend/api';
import {
  Box,
  Text,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Grid,
  GridItem,
  ArrowLeftAndRightIcon,
  ArrowUpAndDownIcon,
} from '@frontend/components';
import { isObject } from '@frontend/utils';

import { UNITS_BY_SYSTEM_AND_MEASURE } from 'constants/unitOfMeasure';

import {
  ErpField,
  GrainDirectionField,
  MeasuredUnitFormControl,
  NumberedFormControl,
  PostOrPrefixInput,
} from '.';
import type {
  FormFieldProps,
  NestingDefaultsData,
  NestingSelectOption,
} from '.';

export const COSTING_UNIT_SHEET_OPTIONS = [
  {
    label: 'Per Sheet',
    value: 'SH',
  },
  {
    label: 'Per Pound',
    value: 'LB',
  },
];
export const COSTING_UNIT_LINEAR_OPTIONS = [
  {
    label: 'Per Piece',
    value: 'PI',
  },
  {
    label: 'Per Inch',
    value: 'IN',
  },
];

type StockPropertiesProps = {
  isSubmitting: boolean;
  isErp: boolean;
  isLinearNest?: boolean;
  unitSystem: PreferredUnitSystem;
};

export const StockProperties = ({
  isSubmitting,
  isErp,
  isLinearNest,
  unitSystem,
}: StockPropertiesProps) => {
  return (
    <>
      <Box>
        <Text fontWeight={600} mb={1.5}>
          Stock Properties
        </Text>
        <>
          {!isLinearNest && (
            <Flex position="relative" gap={0.5} mb={2}>
              <Field name="stockLength">
                {({
                  field,
                  form,
                }: FormFieldProps<NestingDefaultsData, string>) => (
                  <FormControl isReadOnly={isSubmitting}>
                    <FormLabel>
                      <Flex gap={0.5}>
                        Length
                        <ArrowLeftAndRightIcon boxSize={'1.25rem'} />
                      </Flex>
                    </FormLabel>
                    <MeasuredUnitFormControl
                      icon2={
                        <PostOrPrefixInput
                          value={UNITS_BY_SYSTEM_AND_MEASURE[unitSystem].length}
                        />
                      }
                      field={field}
                      form={form}
                      ariaLabel="Nesting Length"
                      unitSystem={unitSystem}
                    />
                  </FormControl>
                )}
              </Field>
              <Text
                alignSelf={'end'}
                mb={0.5}
                color={'feature.secondary'}
                size={'lg'}
              >
                &#x2715;
              </Text>
              <Field name="stockWidth">
                {({
                  field,
                  form,
                }: FormFieldProps<NestingDefaultsData, string>) => (
                  <FormControl isReadOnly={isSubmitting}>
                    <FormLabel>
                      <Flex gap={0.5}>
                        Width
                        <ArrowUpAndDownIcon boxSize={'1.25rem'} />
                      </Flex>
                    </FormLabel>
                    <MeasuredUnitFormControl
                      icon2={
                        <PostOrPrefixInput
                          value={UNITS_BY_SYSTEM_AND_MEASURE[unitSystem].length}
                        />
                      }
                      field={field}
                      form={form}
                      unitSystem={unitSystem}
                      ariaLabel="Nesting Width"
                    />
                  </FormControl>
                )}
              </Field>
            </Flex>
          )}
          <Grid templateColumns="repeat(2, 1fr)" gap={2.5}>
            {isLinearNest && (
              <GridItem>
                <Field name="tubeLength">
                  {({
                    field,
                    form,
                  }: FormFieldProps<NestingDefaultsData, string>) => (
                    <FormControl isReadOnly={isSubmitting}>
                      <FormLabel>
                        <Flex gap={0.5}>
                          Tube Length
                          <ArrowLeftAndRightIcon boxSize={'1.25rem'} />
                        </Flex>
                      </FormLabel>
                      <MeasuredUnitFormControl
                        icon2={
                          <PostOrPrefixInput
                            value={
                              UNITS_BY_SYSTEM_AND_MEASURE[unitSystem].length
                            }
                          />
                        }
                        field={field}
                        form={form}
                        ariaLabel="Nesting Tube Length"
                        unitSystem={unitSystem}
                      />
                    </FormControl>
                  )}
                </Field>
              </GridItem>
            )}
            {isErp && (
              <GridItem>
                <Field name="erpCode">
                  {({ field }: FormFieldProps<NestingDefaultsData, string>) => {
                    return (
                      <ErpField field={field} isSubmitting={isSubmitting} />
                    );
                  }}
                </Field>
              </GridItem>
            )}
            {!isLinearNest && (
              <GridItem>
                <Field name="grainDirection">
                  {({
                    field,
                    form,
                  }: FormFieldProps<NestingDefaultsData, string | null>) => (
                    <GrainDirectionField
                      field={field}
                      form={form}
                      isSubmitting={isSubmitting}
                    />
                  )}
                </Field>
              </GridItem>
            )}
            <GridItem>
              <FormControl isReadOnly={isSubmitting}>
                <Flex>
                  <FormLabel>Cost</FormLabel>
                </Flex>
                <Flex>
                  <Box flexBasis={'50%'}>
                    <Field name="costDollars">
                      {({
                        field,
                        form,
                      }: FormFieldProps<NestingDefaultsData, string>) => (
                        <NumberedFormControl
                          icon1={<PostOrPrefixInput value="$" />}
                          field={field}
                          form={form}
                          precision={2}
                        />
                      )}
                    </Field>
                  </Box>
                  <Box flexBasis={'50%'}>
                    <Field name="costingUnit">
                      {({
                        field,
                        form,
                      }: FormFieldProps<NestingDefaultsData, string>) => {
                        return (
                          <Select
                            value={
                              isObject(field.value)
                                ? field.value
                                : (isLinearNest
                                    ? COSTING_UNIT_LINEAR_OPTIONS
                                    : COSTING_UNIT_SHEET_OPTIONS
                                  ).find((opt) => field.value === opt.value) ??
                                  ''
                            }
                            isClearable={false}
                            isDisabled={isSubmitting}
                            onChange={(opt: NestingSelectOption) =>
                              form.setFieldValue(
                                'costingUnit',
                                opt.value,
                                false,
                              )
                            }
                            options={
                              isLinearNest
                                ? COSTING_UNIT_LINEAR_OPTIONS
                                : COSTING_UNIT_SHEET_OPTIONS
                            }
                          />
                        );
                      }}
                    </Field>
                  </Box>
                </Flex>
              </FormControl>
            </GridItem>
          </Grid>
        </>
      </Box>
    </>
  );
};
